import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageCircle, FiX } = FiIcons;

/**
 * Single entry point to conversational AI for the patient dashboard.
 * Renders as a floating button only — never the default view, per
 * "don't make chat the homepage." Placeholder response until the real
 * assistant endpoint exists; swap the reply in `send` when it does.
 */
const AskIVivaCoreAI = ({ role = 'patient' }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');

  const send = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    const userMsg = { id: Date.now(), role: 'user', content: draft };
    setMessages((prev) => [
      ...prev,
      userMsg,
      {   
        id: Date.now() + 1,
        role: 'assistant',
        content:
          role === 'patient'
            ? "I'll be able to explain what's behind your numbers here once the AI assistant is connected."
            : "I'll be able to explain model output and rationale here once conversational reasoning is connected.",
      },
    ]);
    setDraft('');
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 flex items-center gap-2 rounded-full bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-primary-900/30 hover:bg-primary-500 transition-colors z-40"
      >
        <SafeIcon icon={FiMessageCircle} /> Ask iVivaCore AI
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25 }}
            className="fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-slate-950 border-l border-slate-800 flex flex-col"
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <p className="text-sm font-bold text-white">iVivaCore AI</p>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white">
                <SafeIcon icon={FiX} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {messages.length === 0 && (
                <p className="text-sm text-slate-500">
                  Ask about anything on your dashboard - I explain what's already been calculated.
                </p>
              )}
              {messages.map((m) => (
                <div key={m.id} className={m.role === 'user' ? 'text-right' : 'text-left'}>
                  <span
                    className={`inline-block rounded-xl px-3 py-2 text-sm ${
                      m.role === 'user' ? 'bg-primary-600 text-white' : 'bg-slate-900 text-slate-200'
                    }`}
                  >
                    {m.content}
                  </span>
                </div>
              ))}
            </div>

            <form onSubmit={send} className="flex gap-2 border-t border-slate-800 p-3">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Ask a question…"
                className="flex-1 rounded-lg bg-slate-900 border border-slate-800 px-3 py-2 text-sm text-white outline-none focus:border-primary-600"
              />
              <button type="submit" className="rounded-lg bg-primary-600 px-3 py-2 text-sm font-bold text-white">
                Send
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AskIVivaCoreAI;